import React, { useState, useEffect, useCallback, useContext, useMemo, createContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logsAPI } from '../../services/api';
import { exportTableToExcel } from '../../utils/excel';
import DateTimeRange, { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import './LogsHub.css';

// The date and time picked above the tabs — every tab's rows and Excel file keep to it
// ({ from, to } as ISO, or nothing for any time).
const WhenContext = createContext({});
const useWhen = () => useContext(WhenContext);

// A row's number down the whole list, across pages.
const srOf = (page, limit, i) => (page - 1) * limit + i + 1;

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });
}

const SHEET_NAMES = {
    audit: 'Audit', sessions: 'Sessions', authentication: 'Authentication', pms: 'PMS',
    prelogin: 'Pre-login', security: 'Security', dhcp: 'DHCP', nat: 'NAT',
};

// The tab's rows with its filters — up to the server's 10,000, its columns — as
// an Excel file (backend logs.controller export, format=json).
function exportExcel(logType, params = {}) {
    const filters = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));
    logsAPI.exportTable(logType, filters)
        .then(r => exportTableToExcel(`${logType}-logs-${new Date().toISOString().slice(0, 10)}`, r.data, { sheetName: SHEET_NAMES[logType] }))
        .catch(() => toast.error('Export failed'));
}

const TABS = [
    { key: 'overview',       label: 'Overview' },
    { key: 'audit',          label: 'Audit' },
    { key: 'sessions',       label: 'Sessions' },
    { key: 'authentication', label: 'Authentication' },
    { key: 'pms',            label: 'PMS' },
    { key: 'prelogin',       label: 'Pre-login' },
    { key: 'security',       label: 'Security' },
    { key: 'dhcp',           label: 'DHCP' },
    { key: 'nat',            label: 'NAT' },
];

export default function LogsHub() {
    const [tab, setTab] = useState('overview');
    const [range, setRange] = useState({ from: '', to: '' });
    const when = useMemo(() => {
        const { from, to } = rangeParams(range);
        return { ...(from ? { from } : {}), ...(to ? { to } : {}) };
    }, [range]);

    return (
        <div className="lh-wrap">
            <div className="lh-header">
                <h1 className="lh-title">Hotspot Logs</h1>
            </div>

            <div className="lh-when">
                <DateTimeRange value={range} onChange={setRange} />
            </div>

            <div className="lh-tabs">
                {TABS.map(t => (
                    <button key={t.key} className={`lh-tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            <WhenContext.Provider value={when}>
                <div className="lh-body">
                    {tab === 'overview'       && <OverviewTab />}
                    {tab === 'audit'          && <AuditTab />}
                    {tab === 'sessions'       && <SessionsTab />}
                    {tab === 'authentication' && <AuthenticationTab />}
                    {tab === 'pms'            && <PmsTab />}
                    {tab === 'prelogin'       && <PreLoginTab />}
                    {tab === 'security'       && <SecurityTab />}
                    {tab === 'dhcp'           && <DhcpTab />}
                    {tab === 'nat'            && <NatTab />}
                </div>
            </WhenContext.Provider>
        </div>
    );
}

function OverviewTab() {
    const when = useWhen();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        logsAPI.getSummary(when)
            .then(r => setData(r.data.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [when]);

    if (loading) return <p className="lh-loading">Loading…</p>;
    if (!data)   return <p className="lh-empty">Failed to load summary.</p>;

    return (
        <div className="lh-cards">
            <StatCard label="Sessions"               value={data.sessions} />
            <StatCard label="Syslog Entries"         value={data.syslogEntries} />
            <StatCard label="PMS Log Entries"        value={data.pmsLogs} />
            <StatCard label="Authorization Sessions" value={data.authSessions} />
            <StatCard label="Audit Log Entries"      value={data.auditLogs} />
            <StatCard label="Security Events"        value={data.securityLogs} />
        </div>
    );
}

function StatCard({ label, value }) {
    return (
        <div className="lh-stat-card">
            <div className="lh-stat-val">{value ?? '—'}</div>
            <div className="lh-stat-label">{label}</div>
        </div>
    );
}

function AuditTab() {
    const when = useWhen();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const [filters, setFilters] = useState({ module: '', action: '', entityType: '' });
    const limit = 50;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit, ...filters, ...when };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const r = await logsAPI.getAuditLogs(params);
            setRows(r.data.data || []);
            setTotal(r.data.pagination?.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters, when]);

    useEffect(() => { setPage(1); }, [when]);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    function handleFilter(e) {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    }

    const pages = Math.ceil(total / limit);

    return (
        <div>
            <div className="lh-filters">
                <select name="module" value={filters.module} onChange={handleFilter}>
                    <option value="">All Modules</option>
                    <option value="routers">Routers (NAS)</option>
                    <option value="plans">Plans</option>
                    <option value="tenants">Tenants</option>
                    <option value="branches">Branches</option>
                    <option value="admins">Admins</option>
                    <option value="settings">Rooms</option>
                </select>
                <input name="action" placeholder="Action (e.g. nas.create)" value={filters.action} onChange={handleFilter} />
                <input name="entityType" placeholder="Entity type" value={filters.entityType} onChange={handleFilter} />
                <button className="lh-clear" onClick={() => { setFilters({ module:'', action:'', entityType:'' }); setPage(1); }}>Clear</button>
                <button className="lh-export" onClick={() => exportExcel('audit', { ...filters, ...when })}>⬇ Export Excel</button>
            </div>

            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="lh-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>Actor</th>
                            <th>Action</th>
                            <th>Module</th>
                            <th>Entity Type</th>
                            <th>Source</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="lh-loading">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={7} className="lh-empty">No records found</td></tr>}
                        {!loading && rows.map((row, i) => (
                            <tr key={row._id}>
                                <td className="lh-sr">{srOf(page, limit, i)}</td>
                                <td className="lh-time">{fmt(row.createdAt)}</td>
                                <td>{row.actorId?.name || row.actorId?.email || row.actorRole || '—'}</td>
                                <td className="lh-mono">{row.action}</td>
                                <td>{row.module || '—'}</td>
                                <td>{row.entityType}</td>
                                <td><span className={`lh-badge lh-badge-${row.source}`}>{row.source}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} pages={pages} total={total} setPage={setPage} />
        </div>
    );
}

function SessionsTab() {
    const navigate = useNavigate();
    const when = useWhen();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const [filters, setFilters] = useState({ status: '', deviceMac: '', username: '' });
    const limit = 50;

    // Debounced search — same 350ms setTimeout+useEffect idiom as Devices.jsx
    const [searchTerm, setSearchTerm] = useState('');
    useEffect(() => {
        const t = setTimeout(() => {
            setFilters(f => ({ ...f, username: searchTerm }));
            setPage(1);
        }, 350);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit, ...filters, ...when };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const r = await logsAPI.getSessions(params);
            setRows(r.data.data || []);
            setTotal(r.data.pagination?.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters, when]);

    useEffect(() => { setPage(1); }, [when]);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    function handleFilter(e) {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    }

    const pages = Math.ceil(total / limit);

    return (
        <div>
            <div className="lh-filters">
                <select name="status" value={filters.status} onChange={handleFilter}>
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="terminated">Terminated</option>
                    <option value="timeout">Timeout</option>
                    <option value="error">Error</option>
                </select>
                <input name="deviceMac" placeholder="MAC address" value={filters.deviceMac} onChange={handleFilter} />
                <input placeholder="Search username…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                <button className="lh-clear" onClick={() => { setFilters({ status:'', deviceMac:'', username:'' }); setSearchTerm(''); setPage(1); }}>Clear</button>
                <button className="lh-export" onClick={() => exportExcel('sessions', { ...filters, ...when })}>⬇ Export Excel</button>
            </div>

            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="lh-sr">Sr. No.</th>
                            <th>Started</th>
                            <th>User</th>
                            <th>MAC</th>
                            <th>IP</th>
                            <th>Auth Method</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={8} className="lh-loading">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={8} className="lh-empty">No records found</td></tr>}
                        {!loading && rows.map((row, i) => (
                            <tr key={row._id}>
                                <td className="lh-sr">{srOf(page, limit, i)}</td>
                                <td className="lh-time">{fmt(row.startTime)}</td>
                                <td>{row.userId?.name || row.userId?.radiusUsername || '—'}</td>
                                <td className="lh-mono">{row.deviceMac}</td>
                                <td className="lh-mono">{row.framedIpAddress || '—'}</td>
                                <td>{row.authMethod || '—'}</td>
                                <td><span className={`lh-badge lh-badge-${row.status}`}>{row.status}</span></td>
                                <td>
                                    <button className="lh-view-btn" onClick={() => navigate(`/logs/sessions/${row.sessionId}`)}>
                                        Investigate
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} pages={pages} total={total} setPage={setPage} />
        </div>
    );
}

function AuthenticationTab() {
    const when = useWhen();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const [filters, setFilters] = useState({ status: '' });
    const limit = 50;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit, ...filters, ...when };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const r = await logsAPI.getAuthenticationLogs(params);
            setRows(r.data.data || []);
            setTotal(r.data.pagination?.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters, when]);

    useEffect(() => { setPage(1); }, [when]);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    function handleFilter(e) {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    }

    const pages = Math.ceil(total / limit);

    return (
        <div>
            <div className="lh-filters">
                <select name="status" value={filters.status} onChange={handleFilter}>
                    <option value="">All Status</option>
                    <option value="SUCCESS">Success</option>
                    <option value="FAILED">Failed</option>
                    <option value="PENDING">Pending</option>
                </select>
                <button className="lh-clear" onClick={() => { setFilters({ status: '' }); setPage(1); }}>Clear</button>
                <button className="lh-export" onClick={() => exportExcel('authentication', { ...filters, ...when })}>⬇ Export Excel</button>
            </div>

            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="lh-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>Source</th>
                            <th>Method</th>
                            <th>User</th>
                            <th>MAC</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="lh-loading">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={7} className="lh-empty">No records found</td></tr>}
                        {!loading && rows.map((row, i) => (
                            <tr key={i}>
                                <td className="lh-sr">{srOf(page, limit, i)}</td>
                                <td className="lh-time">{fmt(row.timestamp)}</td>
                                <td>{row.source}</td>
                                <td>{row.method || '—'}</td>
                                <td>{row.username || '—'}</td>
                                <td className="lh-mono">{row.mac || '—'}</td>
                                <td><span className={`lh-badge lh-badge-${(row.status || '').toLowerCase()}`}>{row.status}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} pages={pages} total={total} setPage={setPage} />
        </div>
    );
}

function PmsTab() {
    const when = useWhen();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const [filters, setFilters] = useState({ provider: '', allowed: '' });
    const limit = 50;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit, ...filters, ...when };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const r = await logsAPI.getPmsLogs(params);
            setRows(r.data.data || []);
            setTotal(r.data.pagination?.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters, when]);

    useEffect(() => { setPage(1); }, [when]);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    function handleFilter(e) {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    }

    const pages = Math.ceil(total / limit);

    return (
        <div>
            <div className="lh-filters">
                <select name="provider" value={filters.provider} onChange={handleFilter}>
                    <option value="">All Providers</option>
                    <option value="capitaland">ODX</option>
                    <option value="winhms">WinHMS</option>
                    <option value="wincloud">WinCloud</option>
                    <option value="idsnext">IDS Next</option>
                    <option value="hotla">HOTLA</option>
                    <option value="hotelkey">HotelKey</option>
                    <option value="innkey">InnKey</option>
                </select>
                <select name="allowed" value={filters.allowed} onChange={handleFilter}>
                    <option value="">Allowed: All</option>
                    <option value="true">Allowed</option>
                    <option value="false">Denied</option>
                </select>
                <button className="lh-clear" onClick={() => { setFilters({ provider:'', allowed:'' }); setPage(1); }}>Clear</button>
                <button className="lh-export" onClick={() => exportExcel('pms', { ...filters, ...when })}>⬇ Export Excel</button>
            </div>

            <p className="lh-hint">For the full request/response detail and filters (room, correlation ID, etc.), see <a href="/pms-logs">PMS Activity Logs</a>.</p>

            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="lh-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>Provider</th>
                            <th>Event</th>
                            <th>Room</th>
                            <th>Allowed</th>
                            <th>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="lh-loading">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={7} className="lh-empty">No records found</td></tr>}
                        {!loading && rows.map((row, i) => (
                            <tr key={row._id}>
                                <td className="lh-sr">{srOf(page, limit, i)}</td>
                                <td className="lh-time">{fmt(row.createdAt)}</td>
                                <td>{row.provider}</td>
                                <td>{row.event}</td>
                                <td>{row.roomNumber || row.roomId || '—'}</td>
                                <td>{row.allowed === true ? '✓' : row.allowed === false ? '✗' : '—'}</td>
                                <td>{row.error || row.odxMessage || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} pages={pages} total={total} setPage={setPage} />
        </div>
    );
}

function SecurityTab() {
    const when = useWhen();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const [filters, setFilters] = useState({ eventType: '', severity: '' });
    const limit = 50;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit, ...filters, ...when };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const r = await logsAPI.getSecurityLogs(params);
            setRows(r.data.data || []);
            setTotal(r.data.pagination?.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters, when]);

    useEffect(() => { setPage(1); }, [when]);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    function handleFilter(e) {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    }

    const pages = Math.ceil(total / limit);

    return (
        <div>
            <div className="lh-filters">
                <select name="eventType" value={filters.eventType} onChange={handleFilter}>
                    <option value="">All Events</option>
                    <option value="login_failed">Login Failed</option>
                    <option value="admin_login_locked">Admin Login Locked</option>
                    <option value="rate_limited">Rate Limited</option>
                    <option value="otp_failed">OTP Failed</option>
                    <option value="otp_abuse">OTP Abuse</option>
                    <option value="coa_forced_disconnect">Forced Disconnect</option>
                    <option value="suspicious_activity">Suspicious Activity</option>
                </select>
                <select name="severity" value={filters.severity} onChange={handleFilter}>
                    <option value="">All Severity</option>
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                </select>
                <button className="lh-clear" onClick={() => { setFilters({ eventType:'', severity:'' }); setPage(1); }}>Clear</button>
                <button className="lh-export" onClick={() => exportExcel('security', { ...filters, ...when })}>⬇ Export Excel</button>
            </div>

            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="lh-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>Event</th>
                            <th>Actor</th>
                            <th>Username / IP</th>
                            <th>Severity</th>
                            <th>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="lh-loading">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={7} className="lh-empty">No security events found</td></tr>}
                        {!loading && rows.map((row, i) => (
                            <tr key={row._id}>
                                <td className="lh-sr">{srOf(page, limit, i)}</td>
                                <td className="lh-time">{fmt(row.createdAt)}</td>
                                <td className="lh-mono">{row.eventType}</td>
                                <td>{row.actorId?.name || row.actorId?.email || row.actorType}</td>
                                <td>{row.username || row.ip || '—'}</td>
                                <td><span className={`lh-badge lh-badge-${row.severity}`}>{row.severity}</span></td>
                                <td>{row.message || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} pages={pages} total={total} setPage={setPage} />
        </div>
    );
}

function DhcpTab() {
    const when = useWhen();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const limit = 50;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const r = await logsAPI.getDhcpLogs({ page, limit, ...when });
            setRows(r.data.data || []);
            setTotal(r.data.pagination?.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, when]);

    useEffect(() => { setPage(1); }, [when]);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    const pages = Math.ceil(total / limit);

    return (
        <div>
            <p className="lh-hint">DHCP events are captured via the syslog pipeline (MikroTik DHCP assign/deassign), not a dedicated log.</p>
            <div className="lh-filters">
                <button className="lh-export" onClick={() => exportExcel('dhcp', when)}>⬇ Export Excel</button>
            </div>
            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="lh-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>NAS</th>
                            <th>Subcategory</th>
                            <th>MAC</th>
                            <th>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="lh-loading">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={6} className="lh-empty">No DHCP events found</td></tr>}
                        {!loading && rows.map((row, i) => (
                            <tr key={row._id}>
                                <td className="lh-sr">{srOf(page, limit, i)}</td>
                                <td className="lh-time">{fmt(row.receivedAt)}</td>
                                <td>{row.nasIdentifier || row.nasIp || '—'}</td>
                                <td>{row.subcategory || '—'}</td>
                                <td className="lh-mono">{row.mac || '—'}</td>
                                <td>{row.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination page={page} pages={pages} total={total} setPage={setPage} />
        </div>
    );
}

function NatTab() {
    const when = useWhen();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const [available, setAvailable] = useState(true);
    const limit = 50;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const r = await logsAPI.getNatLogs({ page, limit, ...when });
            setRows(r.data.data || []);
            setTotal(r.data.pagination?.total || 0);
            setAvailable(r.data.natTranslationAvailable !== false);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, when]);

    useEffect(() => { setPage(1); }, [when]);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    const pages = Math.ceil(total / limit);

    return (
        <div>
            {!available && (
                <div className="lh-banner">
                    Public-IP/NAT translation capture (CGNAT/IPDR-style private-IP → public-IP:port mapping) is not yet
                    implemented — the table below shows pre-NAT source/destination IP:port only, as currently captured
                    from router firewall logs.
                </div>
            )}
            <div className="lh-filters">
                <button className="lh-export" onClick={() => exportExcel('nat', when)}>⬇ Export Excel</button>
            </div>
            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="lh-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>NAS IP</th>
                            <th>Source IP:Port</th>
                            <th>Destination IP:Port</th>
                            <th>Protocol</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="lh-loading">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={6} className="lh-empty">No entries found</td></tr>}
                        {!loading && rows.map((row, i) => (
                            <tr key={row._id}>
                                <td className="lh-sr">{srOf(page, limit, i)}</td>
                                <td className="lh-time">{fmt(row.receivedAt)}</td>
                                <td className="lh-mono">{row.nasIp || '—'}</td>
                                <td className="lh-mono">{row.srcIp}{row.srcPort ? `:${row.srcPort}` : ''}</td>
                                <td className="lh-mono">{row.dstIp}{row.dstPort ? `:${row.dstPort}` : ''}</td>
                                <td>{row.protocol || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination page={page} pages={pages} total={total} setPage={setPage} />
        </div>
    );
}

// Internet before sign-in (Branches → Internet before sign-in): each step of each
// device — the page opened, a WhatsApp code, a payment, the sign-in that closed it.
const PRELOGIN_STEP = { connect: 'Page opened', otp: 'WhatsApp code', payment: 'Payment', signin: 'Signed in' };
const PRELOGIN_REFUSAL = {
    'no-device': 'No device MAC',
    limit: "Day's limit used",
    unsupported: 'No Router API',
    'signed-in': 'Already signed in',
    error: 'Router error',
};
// An iPhone's page waits this long for its window (backend portal.controller).
const IPHONE_WAIT_MS = 5000;

function PreLoginResult({ row }) {
    if (row.outcome === 'granted') return <span className="lh-badge lh-badge-success">Window opened · {row.minutes} min</span>;
    if (row.outcome === 'reused')  return <span className="lh-badge lh-badge-info">Window still open</span>;
    if (row.outcome === 'ended')   return <span className="lh-badge lh-badge-neutral">Closed — signed in</span>;
    return <span className="lh-badge lh-badge-failed">Refused: {PRELOGIN_REFUSAL[row.refusal] || row.refusal || '—'}</span>;
}

function PreLoginTab() {
    const when = useWhen();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const [filters, setFilters] = useState({ mac: '', mobile: '', outcome: '', reason: '' });
    const limit = 50;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit, ...filters, ...when };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const r = await logsAPI.getPreLoginLogs(params);
            setRows(r.data.data || []);
            setTotal(r.data.pagination?.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters, when]);

    useEffect(() => { setPage(1); }, [when]);
    useEffect(() => { fetchRows(); }, [fetchRows]);

    function handleFilter(e) {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    }
    // One device's (or one guest's) whole log.
    const only = (name, value) => { if (value) { setFilters({ mac: '', mobile: '', outcome: '', reason: '', [name]: value }); setPage(1); } };

    const pages = Math.ceil(total / limit);

    return (
        <div>
            <div className="lh-filters">
                <input name="mac" placeholder="MAC address" value={filters.mac} onChange={handleFilter} />
                <input name="mobile" placeholder="Mobile number" value={filters.mobile} onChange={handleFilter} />
                <select name="reason" value={filters.reason} onChange={handleFilter}>
                    <option value="">All steps</option>
                    {Object.entries(PRELOGIN_STEP).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
                <select name="outcome" value={filters.outcome} onChange={handleFilter}>
                    <option value="">All results</option>
                    <option value="granted">Window opened</option>
                    <option value="reused">Window still open</option>
                    <option value="refused">Refused</option>
                    <option value="ended">Closed — signed in</option>
                </select>
                <button className="lh-clear" onClick={() => { setFilters({ mac: '', mobile: '', outcome: '', reason: '' }); setPage(1); }}>Clear</button>
                <button className="lh-export" onClick={() => exportExcel('prelogin', { ...filters, ...when })}>⬇ Export Excel</button>
            </div>

            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="lh-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>Branch</th>
                            <th>Mobile</th>
                            <th>Device · IP</th>
                            <th>Step</th>
                            <th>Result</th>
                            <th>Router</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={8} className="lh-loading">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={8} className="lh-empty">No records found</td></tr>}
                        {!loading && rows.map((row, i) => {
                            const slowForIphone = row.reason === 'connect' && /^iP(hone|ad|od)/.test(row.device || '') && row.durationMs > IPHONE_WAIT_MS;
                            return (
                                <tr key={row._id}>
                                    <td className="lh-sr">{srOf(page, limit, i)}</td>
                                    <td className="lh-time">{fmt(row.createdAt)}</td>
                                    <td>{row.branchId?.name || '—'}</td>
                                    <td>{row.mobile
                                        ? <button className="lh-link" title="This guest's log only" onClick={() => only('mobile', row.mobile)}>{row.mobile}</button>
                                        : '—'}</td>
                                    <td className="lh-nowrap" title={row.userAgent || ''}>
                                        {row.mac
                                            ? <button className="lh-link lh-mono" title="This device's log only" onClick={() => only('mac', row.mac)}>{row.mac}</button>
                                            : '—'}
                                        <div className="lh-note">{[row.ip, row.device].filter(Boolean).join(' · ') || '—'}</div>
                                    </td>
                                    <td>{PRELOGIN_STEP[row.reason] || row.reason}</td>
                                    <td title={row.detail || ''}>
                                        <PreLoginResult row={row} />
                                        {row.outcome === 'refused' && row.detail && <div className="lh-note">{row.detail}</div>}
                                        {row.expiresAt && row.outcome !== 'ended' && <div className="lh-note">until {fmt(row.expiresAt)}</div>}
                                    </td>
                                    <td>
                                        <span className="lh-mono">{row.nasId?.nasIdentifier || row.nasId?.name || '—'}</span>
                                        {row.durationMs != null && <div className="lh-note" title="How long the router took to answer">answered in {row.durationMs} ms</div>}
                                        {slowForIphone && <div className="lh-note lh-note-warn">Over 5 s: the iPhone's page was not told</div>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} pages={pages} total={total} setPage={setPage} />
        </div>
    );
}

function Pagination({ page, pages, total, setPage }) {
    return (
        <div className="lh-pagination">
            <span className="lh-total">Total: {total}</span>
            <button disabled={page <= 1}     onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span>Page {page} / {pages || 1}</span>
            <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
        </div>
    );
}
